import React, {
  FC,
  ReactElement,
  MouseEvent,
  useMemo,
  useState,
  useCallback,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react'

import { useTheme } from 'styled-components'

import { definitions } from 'api/generated/photo'
import { NumberDirection } from 'common-constants/numbers'
import { findCurrentIndex } from 'components/page/PhotoViewer/function/findCurrentIndex'
import { PhotoViewerPhotosWrapper } from 'components/page/PhotoViewer/PhotoViewerPhotos/PhotoViewerPhotosWrapper'
import { translateX } from 'functions/layout/translate'

import {
  PhotoViewerContext,
  PhotoViewerContextData,
} from './PhotoViewerCarousel.context'
import { PhotoViewerPromoCase } from './PhotoViewerCarousel.types'
import { PhotoViewerSwiper } from './PhotoViewerSwiper'
import { enableAnimation } from './PhotoViewerSwiper.functions'
import { findNextIndex } from '../function/findNextIndex'
import { PhotoViewerNoticeView } from '../PhotoViewerNotice/PhotoViewerNoticeView'

/**
 * Компонент который собирает вместе:
 * - свайпилку фоток
 * - отображение фоток
 * - накладку промо
 */
export const PhotoViewerCarousel: FC<{
  photoId: string
  photos: definitions['OmniAlbumPhoto'][]
  photoBaseUrl: string

  notice?: ReactElement
  showNotice: boolean
  showArrows: boolean
  /** Если вернули из подарков, должны увидеть промо */
  forceShowPromo?: boolean

  togglePanels?: (value: boolean) => void
  updateCurrentIndex: (value: number) => void
  onPhotoClick?: (event: MouseEvent<HTMLDivElement>) => void
}> = ({
  photoId,
  photos,
  photoBaseUrl,

  notice,
  showNotice,
  showArrows,
  forceShowPromo,

  togglePanels,
  updateCurrentIndex,
  onPhotoClick,
}) => {
  const { multiplier } = useTheme()
  const photosLength = useMemo(() => photos.length - 1, [photos.length])

  const [currentIndex, setCurrentIndex] = useState<number>(
    findCurrentIndex(photos, photoId)
  )

  const updateIndex = useCallback(
    (direction: number) => {
      setCurrentIndex((value) => {
        if (value + direction < 0) {
          updateCurrentIndex(photosLength)
          return photosLength
        } else if (value + direction > photosLength) {
          updateCurrentIndex(0)
          return 0
        } else {
          const index = value + direction
          updateCurrentIndex(index)
          return index
        }
      })
    },
    [photosLength, updateCurrentIndex]
  )

  const promoRef = useRef<HTMLDivElement>(null)

  const movePromoX = useCallback((value: number) => {
    if (promoRef.current) {
      translateX(promoRef.current, value)
    }
  }, [])

  const showPromo = useCallback((value: boolean) => {
    if (promoRef.current) {
      promoRef.current.style.display = value ? 'flex' : 'none'
    }
  }, [])

  /**
   * Специально убрал зависимость от currentIndex, чтобы считалось только
   * для значения в первом рендере.
   *
   * Логика промки это индекс - 1 от фотки с которой открыли фотовьювер.
   */
  const promoIndex = useMemo(() => {
    /** Если вернулись с гифтов, то значит выбрана промка */
    if (forceShowPromo) {
      return currentIndex
    }
    /**
     * У нас есть фотки [1, 2, 3]
     * Зашли на 2, в итоге показываем промку когда следующий раз оказываемся на 1.
     * Т.е. -1 это как раз будет индекс элемента 1.
     */
    return findNextIndex(-1, currentIndex, photos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceShowPromo, photos])
  const [promoCase, setPromoCase] = useState<PhotoViewerPromoCase | null>(null)
  const forcedRef = useRef(false)

  useLayoutEffect(() => {
    if (forceShowPromo && !forcedRef.current) {
      setPromoCase(PhotoViewerPromoCase.prevoiusFinished)
      forcedRef.current = true
      // пересчитать индекс промо?
    } else if (!showNotice) {
      setPromoCase(null)
    } else if (currentIndex === promoIndex) {
      /**
       * Накладываем слева направо промку
       */
      setPromoCase((value) => {
        /**
         * Проверка внутри, чтобы не перезапускать эффект.
         * Если добавить в зависимость эффекта promoCase, то сломается.
         */
        if (value === PhotoViewerPromoCase.prevoiusFinished) {
          return value
        }
        return PhotoViewerPromoCase.next
      })
    } else if (
      findNextIndex(
        NumberDirection.left /** multiplier не нужен */,
        currentIndex,
        photos
      ) === promoIndex
    ) {
      /**
       * Промка выезжает на предыдущей фотке, двигаясь вместе с ней
       */
      setPromoCase(PhotoViewerPromoCase.prevoius)
    } else {
      // console.log(
      //   'next index',
      //   findNextIndex(NumberDirection.left * multiplier, currentIndex, photos),
      //   NumberDirection.left * multiplier,
      //   { currentIndex, length: photos.length }
      // )
      setPromoCase(null)
    }
  }, [currentIndex, forceShowPromo, multiplier, photos, promoIndex, showNotice])

  // console.log({ currentIndex, promoCase, promoIndex })

  const value = useMemo<PhotoViewerContextData>(
    () => ({
      photos,
      showArrows,
      photoBaseUrl,
      currentIndex,
    }),
    [currentIndex, photoBaseUrl, photos, showArrows]
  )

  useEffect(() => {
    togglePanels?.(
      ![
        PhotoViewerPromoCase.nextFinished,
        PhotoViewerPromoCase.prevoiusFinished,
      ].includes(promoCase!)
    )
  }, [promoCase, togglePanels])

  const handlePromoAnimation = useCallback((value) => {
    if (promoRef.current) {
      enableAnimation(promoRef.current, value)
    }
  }, [])

  const singlePhoto = photos.length === 1

  // console.log('PhotoViewerCarousel render', { currentIndex, promoCase })

  return (
    <PhotoViewerContext.Provider value={value}>
      <PhotoViewerSwiper
        singlePhoto={singlePhoto}
        promoCase={promoCase}
        onUpdateView={updateIndex}
        onMovePromoX={movePromoX}
        onPromoAnimation={handlePromoAnimation}
        onShowPromo={showPromo}
        onUpdatePromoCase={setPromoCase}
      >
        <PhotoViewerPhotosWrapper
          photos={photos}
          currentIndex={currentIndex}
          promoCase={singlePhoto && promoCase}
          isShowNotice={showNotice}
          onPhotoClick={onPhotoClick}
        />

        {showNotice && (
          <PhotoViewerNoticeView ref={promoRef}>{notice}</PhotoViewerNoticeView>
        )}
      </PhotoViewerSwiper>
    </PhotoViewerContext.Provider>
  )
}
